/**
 * iter212 — WhatsApp Message Log card.
 *
 * Displays the recent outbound WhatsApp messages with their latest Meta
 * delivery status (sent → delivered → read → failed). Used in two places:
 *
 *  • WhatsAppSettingsPage (super-admin) — global view across all groups
 *    via GET /api/whatsapp/messages
 *  • GroupDetailPage notifications tab (group admin) — scoped to one group
 *    via GET /api/whatsapp/groups/{group_id}/messages
 *
 * Props:
 *   apiPath:    full backend path to fetch (e.g. '/whatsapp/messages' or
 *               `/whatsapp/groups/${groupId}/messages`)
 *   title:      card heading
 *   refreshInterval: ms (default 30_000) — auto-refresh cadence
 *   allowRetry: if true, shows a "Retry" button on `failed` rows
 *   defaultLimit:  rows to fetch (default 50)
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RefreshCw, Loader2, CheckCircle2, XCircle, Send, Check, CheckCheck, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  sent: { label: 'Sent', color: 'text-slate-500', icon: Check },
  delivered: { label: 'Delivered', color: 'text-blue-500', icon: CheckCheck },
  read: { label: 'Read', color: 'text-emerald-500', icon: CheckCheck },
  failed: { label: 'Failed', color: 'text-red-500', icon: XCircle },
  undeliverable: { label: 'Undeliverable', color: 'text-red-500', icon: XCircle },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', color: 'text-muted-foreground', icon: Send };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-bold uppercase ${meta.color}`}
      data-testid={`msg-status-${status || 'unknown'}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function WhatsAppMessageLogCard({
  apiPath,
  title = 'WhatsApp Message Log',
  refreshInterval = 30000,
  allowRetry = true,
  defaultLimit = 50,
  testIdPrefix = 'wa-msglog',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | failed
  const [phoneFilter, setPhoneFilter] = useState('');
  const [retryingId, setRetryingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(defaultLimit));
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (phoneFilter.trim()) params.set('phone', phoneFilter.trim());
      const res = await api.get(`${apiPath}?${params.toString()}`);
      setRows(res.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load message log');
    } finally {
      setLoading(false);
    }
  }, [apiPath, defaultLimit, statusFilter, phoneFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!refreshInterval) return undefined;
    const id = setInterval(() => fetchRows(), refreshInterval);
    return () => clearInterval(id);
  }, [refreshInterval, fetchRows]);

  const handleRetry = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Re-send to ${row.to_user_name || row.to_phone}?\n\nBody preview: "${(row.body_preview || '').slice(0, 100)}"`)) return;
    setRetryingId(row.id);
    try {
      const res = await api.post(`/whatsapp/messages/${row.id}/retry`);
      if (res.data?.ok) {
        toast.success(`Re-sent · new wamid: ${(res.data?.new_message_id || '').slice(0, 22)}…`);
        fetchRows();
      } else {
        const r = res.data?.retry_result || {};
        toast.error(`Retry failed: ${r.error || 'unknown'} (code ${r.code || '?'})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const failedCount = rows.filter((r) => r.status === 'failed').length;
  const deliveredCount = rows.filter((r) => r.status === 'delivered' || r.status === 'read').length;
  const sentCount = rows.filter((r) => r.status === 'sent').length;

  return (
    <Card className="p-5 border-2 rounded-2xl space-y-3" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircle className="w-5 h-5 text-indigo-500 shrink-0" />
          <h3 className="font-semibold text-lg truncate">{title}</h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchRows}
          disabled={loading}
          data-testid={`${testIdPrefix}-refresh`}
          className="rounded-full text-xs h-8 shrink-0"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1 hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 text-center" data-testid={`${testIdPrefix}-stats`}>
        <div className="rounded-xl border-2 border-blue-500/30 bg-blue-500/5 p-2">
          <p className="text-lg font-bold text-blue-600">{deliveredCount}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Delivered/Read</p>
        </div>
        <div className="rounded-xl border-2 border-slate-300/40 bg-slate-300/5 p-2">
          <p className="text-lg font-bold text-slate-600">{sentCount}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Sent</p>
        </div>
        <div className="rounded-xl border-2 border-red-500/30 bg-red-500/5 p-2">
          <p className="text-lg font-bold text-red-600">{failedCount}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Failed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
          <div className="inline-flex rounded-full border-2 border-border bg-card p-0.5 w-full" data-testid={`${testIdPrefix}-status-toggle`}>
            {[
              { id: 'all', label: 'All' },
              { id: 'failed', label: 'Failed' },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatusFilter(s.id)}
                data-testid={`${testIdPrefix}-status-${s.id}`}
                className={`flex-1 px-2 h-7 rounded-full text-xs font-semibold transition-colors ${
                  statusFilter === s.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Phone</Label>
          <Input
            value={phoneFilter}
            onChange={(e) => setPhoneFilter(e.target.value)}
            placeholder="e.g. 9820"
            data-testid={`${testIdPrefix}-phone-filter`}
            className="h-7 border-2 rounded-full text-xs"
          />
        </div>
      </div>

      {/* Rows */}
      <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground italic p-3 border rounded-xl bg-muted/20" data-testid={`${testIdPrefix}-empty`}>
            No messages yet. Outbound WhatsApp will appear here once Meta confirms delivery.
          </p>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              className="rounded-xl border p-2 text-xs"
              data-testid={`${testIdPrefix}-row-${row.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">
                    {row.to_user_name || 'Unknown'} <span className="text-muted-foreground font-normal">·{row.to_phone?.slice(-4) || ''}</span>
                  </p>
                  {(row.notif_type || row.template_name) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {row.template_name || row.notif_type}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <StatusPill status={row.status} />
                  <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(row.sent_at)}</p>
                </div>
              </div>
              {row.body_preview && (
                <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{row.body_preview}</p>
              )}
              {row.status === 'failed' && (row.error_code || row.error_title) && (
                <p className="text-xs text-red-600 mb-1" data-testid={`${testIdPrefix}-error-${row.id}`}>
                  ⚠️ {row.error_code ? `[${row.error_code}] ` : ''}{row.error_title || 'Unknown error'}
                </p>
              )}
              {allowRetry && row.status === 'failed' && !row.template_name && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRetry(row)}
                  disabled={retryingId === row.id}
                  data-testid={`${testIdPrefix}-retry-${row.id}`}
                  className="h-6 px-2 rounded-full text-xs"
                >
                  {retryingId === row.id ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      Retrying…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Retry
                    </>
                  )}
                </Button>
              )}
              {allowRetry && row.status === 'failed' && row.template_name && (
                <p className="text-xs text-muted-foreground italic" data-testid={`${testIdPrefix}-no-retry-${row.id}`}>
                  Template retries unavailable — re-trigger the original event instead.
                </p>
              )}
            </div>
          ))
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Showing {rows.length} most recent · Auto-refreshes every {Math.round((refreshInterval || 0) / 1000)}s
      </p>
    </Card>
  );
}
